import type { IShare, ISubscription } from '@wovin/core'
import type { AgentStateClass } from './AgentState'

import { Logger } from 'besonders-logger'
import { createStore, reconcile } from 'solid-js/store'
import { notifyToast } from '../../ui/utils-ui'
import { stateDB } from '../local-state'
import { useAgent } from './AgentState'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.INFO) // eslint-disable-line unused-imports/no-unused-vars

// Module-scoped Solid stores so consumers in components react to mutations.
// (Replaces the reactivity that the old MobX→Solid external-source bridge gave us.)
const [_shares, setSharesStore] = createStore<IShare[]>([])
const [_subscriptions, setSubscriptionsStore] = createStore<ISubscription[]>([])

export class AgentPubSub {
	mixed = this as unknown as AgentStateClass
	get shares(): IShare[] { return _shares }
	get subscriptions(): ISubscription[] { return _subscriptions }

	get sharesMap() {
		return new Map(this.shares.map(share => [share.id, share]))
	}

	get subscriptionsMap() {
		return new Map(this.subscriptions.map(sub => [sub.id, sub]))
	}

	get mostRecentShare() {
		if (!this.shares.length) return null
		return this.shares[this.shares.length - 1]
	}

	getDirectoryHandle = async function (this: AgentStateClass) {
		return (await stateDB.directoryHandles.get(this.ag))?.directoryHandle
	}

	setDirectoryHandle = async function (this: AgentStateClass, directoryHandle: FileSystemDirectoryHandle) {
		LOG(`Set Handle:`, directoryHandle)
		// (i) dexie does some type checking, so we do it first & await
		await stateDB.directoryHandles.put({ ag: this.ag, directoryHandle })
	}

	addShare = async function (this: AgentStateClass, share: IShare) {
		LOG(`Adding share:`, share)
		// (i) dexie does some type checking, so we do it first & await
		await stateDB.shares.add(share)
		this.reloadPubSub()
	}

	addSub = async function (this: AgentStateClass, sub: ISubscription) {
		LOG(`Adding subscription:`, sub)
		await stateDB.subscriptions.add(sub)
		this.reloadPubSub()
	}

	updateSub = async function (this: AgentStateClass, id: string, changes: Partial<Omit<ISubscription, 'id'>>) {
		const idx = _subscriptions.findIndex(p => p.id === id)
		if (idx === -1) throw ERROR(`[updateSub] id not found:`, id, changes)
		LOG(`Updating subscription:`, id, changes, _subscriptions[idx])
		await stateDB.subscriptions.update(id, changes)
		setSubscriptionsStore(idx, changes)
	}

	updateShare = async function (this: AgentStateClass, id: string, changes: Partial<Omit<IShare, 'id'>>) {
		const idx = _shares.findIndex(p => p.id === id)
		if (idx === -1) throw ERROR(`[updateShare] id not found:`, id, changes)
		const share = _shares[idx]
		const { publishedBy } = share
		const { ag } = useAgent()
		if (ag !== publishedBy) {
			WARN(`[updateShare] last push was a different agent`, { publishedBy, ag })
			notifyToast(
				`This share was last pushed by a different agent (${publishedBy} --> ${ag}) if you did not change your agent string, something may be up with your DID situation`,
				'warning',
			)
			changes.publishedBy = ag
		}
		LOG(`Updating share:`, id, changes, share)
		void stateDB.shares.update(id, changes)
		setSharesStore(idx, changes)
	}

	deleteShare = async function (this: AgentStateClass, shareID: string) {
		const share = this.shares.find(p => p.id === shareID)
		LOG(`Deleting share:`, shareID, share)
		if (!share) throw ERROR(`Trying to delete non-existing share:`, shareID)
		// await stateDB.shares.update(shareID, { isDeleted: true })
		await stateDB.shares.delete(shareID)
		this.reloadPubSub()
	}

	deleteSub = async function (this: AgentStateClass, subID: string) {
		const sub = this.subscriptions.find(p => p.id === subID)
		LOG(`Deleting subscription:`, subID, sub)
		if (!sub) throw ERROR(`Trying to delete non-existing subscription:`, subID)
		// Stop watcher if active
		const { stopWatcher } = await import('../../ipfs/sync-service')
		stopWatcher(subID)
		// await stateDB.subscriptions.update(subID, { isDeleted: true })
		await stateDB.subscriptions.delete(subID)
		this.reloadPubSub()
	}

	reloadPubSub = async () => {
		const shares = (await stateDB.shares.orderBy(/* 'lastPush',  */ 'createdAt').toArray()).filter(p => !p.isDeleted)
		const subscriptions = (await stateDB.subscriptions.orderBy(/* 'lastPull',  */ 'createdAt').toArray()).filter(p => !p.isDeleted)
		// `reconcile` keeps stable identity for items already present (matched by id),
		// so <For> reuses existing rows instead of remounting on every reload.
		setSharesStore(reconcile(shares, { key: 'id' }))
		setSubscriptionsStore(reconcile(subscriptions, { key: 'id' }))
		DEBUG(`Loaded from IDB:`, { shares: this.shares, subs: this.subscriptions, stateDB })
	}
}
